import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import snakecaseKeys from 'snakecase-keys';
import camelcaseKeys from 'camelcase-keys';
import { API_HOST, getRequestConfig } from '~/api/utils.ts';
import { globalStore } from '~/stores';
import { AppContexts } from '~/utils/commonUtils.ts';

type ResponseTransformer<T> = (r: AxiosResponse<T>) => AxiosResponse<T>;

type RequestOptions = {
  beforeRequest?: (
    config: ScalekitInternalAxiosRequestConfig
  ) => ScalekitInternalAxiosRequestConfig | void;
  onResponseSuccess?: ResponseTransformer<any>;
  onResponseError?: (error: any) => void;
};

export interface ScalekitAxiosConfig extends AxiosRequestConfig {
  envDomain?: string;
  addEnvDomain?: boolean;
  addXRequestedWith?: boolean;
  camelCaseResponse?: boolean;
  snakeCaseRequest?: boolean;
}

interface ScalekitInternalAxiosRequestConfig
  extends InternalAxiosRequestConfig {
  envDomain?: string;
  addEnvDomain?: boolean;
  addXRequestedWith?: boolean;
  camelCaseResponse?: boolean;
  snakeCaseRequest?: boolean;
}

const HEADER_KEY_SKIP_REQUEST_TRANSFORMATION = 'skip-request-transformation';

class Request {
  private static instance: AxiosInstance;
  constructor(options?: RequestOptions) {
    Request.instance = axios.create({
      baseURL: `${API_HOST}/api/v1`,
      headers: {
        'Content-Type': 'application/json'
      },
      transformRequest: [
        function (data, headers) {
          // Specifically done for apis like svix where the snake casing is not needed
          if (headers.get(HEADER_KEY_SKIP_REQUEST_TRANSFORMATION) === 'true') {
            headers.delete(HEADER_KEY_SKIP_REQUEST_TRANSFORMATION);
            return JSON.stringify(data);
          }
          return headers['Content-Type'] === 'application/json'
            ? JSON.stringify(
                typeof data === 'object' ? snakecaseKeys(data) : data
              )
            : data;
        }
      ],
      transformResponse: [(data) => data]
    });

    // @todo use getRequestHeaders from utils
    Request.instance.interceptors.request.use(
      (config: ScalekitInternalAxiosRequestConfig) => {
        const requestConfig = {
          ...getRequestConfig({}),
          ...config
        };
        const finalConfig =
          options?.beforeRequest?.(requestConfig) ?? requestConfig;
        if (finalConfig.addXRequestedWith) {
          finalConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        }
        if (finalConfig.addEnvDomain) {
          if (globalStore.appContext === AppContexts.SCALEKIT_DASHBOARD) {
            finalConfig.headers['x-env-domain'] =
              finalConfig.envDomain || globalStore.currentEnvDomain;
          } else if (
            globalStore.appContext === AppContexts.CUSTOMER_PORTAL &&
            globalStore.portalAuthToken
          ) {
            finalConfig.headers['Authorization'] =
              `Bearer ${globalStore.portalAuthToken}`;
          }
        }
        if (finalConfig.snakeCaseRequest === false) {
          finalConfig.headers[HEADER_KEY_SKIP_REQUEST_TRANSFORMATION] = true;
        }
        return finalConfig;
      }
    );

    Request.instance.interceptors.response.use(
      (response) => {
        if (response.data) {
          response.data = (
            response.config as ScalekitInternalAxiosRequestConfig
          ).camelCaseResponse
            ? JSON.stringify(
                camelcaseKeys(JSON.parse(response.data), {
                  deep: true
                })
              )
            : response.data;
          options?.onResponseSuccess && options.onResponseSuccess(response);
        }
        return response;
      },
      (error) => {
        options?.onResponseError && options.onResponseError(error);
        // Let the error get forwarded to the application
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): AxiosInstance {
    if (!Request.instance) {
      new Request();
    }
    return Request.instance;
  }
}

export default Request;
